// Matters — named sub-groups of a project's work, each invoiceable on its own
// (migration 0061). Writing one renames or closes what every time entry,
// expense and invoice shows, so mutations refresh those lists too.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProjectMatter, ProjectMattersListResponse } from '@stfors/shared-types';
import { api } from '../client';

export function useMatters(projectId: number | null | undefined) {
  return useQuery({
    queryKey: ['project-matters', { project_id: projectId ?? null }],
    queryFn: ({ signal }) =>
      api.get<ProjectMattersListResponse>(`/api/project-matters?project_id=${projectId}`, signal),
    enabled: typeof projectId === 'number' && projectId > 0,
  });
}

function invalidateMatterReaders(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['project-matters'] });
  void qc.invalidateQueries({ queryKey: ['time-entries'] });
  void qc.invalidateQueries({ queryKey: ['expenses'] });
  void qc.invalidateQueries({ queryKey: ['invoices'] });
}

export interface CreateMatterInput {
  project_id: number;
  name: string;
  description?: string | null;
}

export function useCreateMatter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMatterInput) =>
      api.post<{ matter: ProjectMatter }>('/api/project-matters', input),
    onSuccess: () => invalidateMatterReaders(qc),
  });
}

export interface UpdateMatterInput {
  id: number;
  name?: string;
  description?: string | null;
  closed?: boolean;
}

export function useUpdateMatter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateMatterInput) =>
      api.patch<{ matter: ProjectMatter }>(`/api/project-matters/${id}`, patch),
    onSuccess: () => invalidateMatterReaders(qc),
  });
}

export function useDeleteMatter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/project-matters/${id}`),
    onSuccess: () => invalidateMatterReaders(qc),
  });
}
